import type { ReactNode } from 'react';
import type { ProductDraft } from '../lib/draft.js';

export type Errors = Record<string, string>;
export type SetField = <K extends keyof ProductDraft>(k: K, v: ProductDraft[K]) => void;
export type Update = (fn: (d: ProductDraft) => ProductDraft, clear?: string) => void;

/** Ids shared by the input, its label and its error, so the first error can take focus. */
export const fid = (name: string) => `pf-${name}`;

/** Props that tie an input to its error message. */
export function invalid(name: string, error?: string) {
  return {
    id: fid(name),
    'aria-invalid': error ? (true as const) : undefined,
    'aria-describedby': error ? `${fid(name)}-err` : undefined,
  };
}

export function FieldError({ name, error }: { name: string; error?: string }) {
  if (!error) return null;
  return (
    <span id={`${fid(name)}-err`} className="pill st-error err-pill cat-wrap">
      {error}
    </span>
  );
}

export function Field({ name, label, error, help, children }: { name: string; label: ReactNode; error?: string; help?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={fid(name)}>{label}</label>
      {children}
      {help && !error ? <span className="help">{help}</span> : null}
      <FieldError name={name} error={error} />
    </div>
  );
}

/** "₱ [ 12.50 ] / kg" */
export function PesoInput({
  name,
  value,
  error,
  suffix,
  label,
  onChange,
}: {
  name: string;
  value: string;
  error?: string;
  suffix?: string;
  label?: string;
  onChange(v: string): void;
}) {
  return (
    <div className="input-affix" data-invalid={error ? 'true' : undefined}>
      <span aria-hidden="true">₱</span>
      <input {...invalid(name, error)} aria-label={label} inputMode="decimal" autoComplete="off" placeholder="0.00" value={value} onChange={(e) => onChange(e.target.value)} />
      {suffix ? <span className="muted" aria-hidden="true">{suffix}</span> : null}
    </div>
  );
}

/** A focusable anchor + message for list-level errors (bands, groups, combo). */
export function ListError({ name, error }: { name: string; error?: string }) {
  return (
    <div id={fid(name)} tabIndex={-1} className="cat-anchor">
      {error ? (
        <span id={`${fid(name)}-err`} className="pill st-error err-pill cat-wrap" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
